import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Typography from "apollo-react/components/Typography";
import Modal from 'apollo-react/components/Modal';
import Button from "apollo-react/components/Button";
import CircularProgress from 'apollo-react/components/CircularProgress';
import Box from 'apollo-react/components/Box';
import NewspaperFold from 'apollo-react-icons/NewspaperFold';
import PresentationSpeaker from 'apollo-react-icons/PresentationSpeaker';
import Planner from 'apollo-react-icons/Planner';

import styles from '../FormApproveComponents/FormSections/FormSections.module.css';
import tabStyles from './CoreECTD.module.css';
import EctdTree from "../ECTD/Ectd";
import EctdVersionsHistory from "../ECTD/EctdVersionsHistory";
import axiosInstance from '../../../axiosInstance';
import { ROLES } from '../../../constants/roles';
import { CATEGORIES } from '../../../constants/categories';
import { STATUSES } from '../../../constants/statuses';

import LaboratoryUpload from '../../Guest/Requests/New_Requests/LaboratoryUpload/LaboratoryUpload';
import LaboratoryView from '../LaboratoryView/LaboratoryView';
import CommitteeReviewButtons from '../FormApproveComponents/CommitteeReviewButtons';
import { canEditLaboratory, canEditInspection } from "../../../utils/uiPermissions";
import InspectionUpload from '../../Guest/Requests/New_Requests/InspectionUpload/InspectionUpload';
import InspectionView from '../InspectionView/InspectionView';
import MembersStatuses from '../../Admin/Approvals/CoreValidator/MembersStatuses';
import CommitteeECTDSection from './CommitteeECTDSection';
import { getCurrentUser as getCurrentUserAdmin } from "../../Admin/services/authService";
import { getCurrentUser as getCurrentUserCustomer } from "../../Guest/services/authService";
import { Paper } from '@mui/material';

const getTabsForRole = (role) => {
    const baseTabs = [{
        label: "eCTD",
        value: 0
    }];
    
    const validatorRoles = [
        ROLES.core_Validator,
        ROLES.technical_validator,
        ROLES.business_validator,
        ROLES.pricing
    ];
    
    const committeeRoles = [
        ROLES.RA,
        ...CATEGORIES.COMMITTEE_CATEGORIES
    ];
    
    const isReviewerOrCommittee = role && (
        validatorRoles.includes(role) || 
        committeeRoles.includes(role) ||
        CATEGORIES.COMMITTEE_CATEGORIES.includes(role)
    );
    
    if (isReviewerOrCommittee) {
        baseTabs.push({
            label: "AI Review Agent",
            value: 1
        });
    }
    
    baseTabs.push({
        label: "Versions",
        value: isReviewerOrCommittee ? 2 : 1
    });
    
    return baseTabs;
};

const Ectd = ({ ectdTreeComponentProps = {}, persona,
    initialUploadedFile = null,
    role = '', ectdFileId = null,
    laboratoryViewComponentProps = {}, laboratoryUploadComponentProps = {},
    inspectionViewComponentProps = {}, inspectionUploadComponentProps = {},
    CommitteeReviewButtonsComponentProps = {}, MembersStatusesComponentProps = {},
    showCommitteeReviewButtons = false, requestProgressId = null, requestId = null }) => {

    const { t } = useTranslation();
    const tabs = getTabsForRole(role);
    const [currentUser, setCurrentUser] = useState(null);
    const [activeTab, setActiveTab] = useState(tabs[0].value);
    const [uploadedFile, setUploadedFile] = useState(initialUploadedFile);
    const [viewStatusModalOpen, setViewStatusModalOpen] = useState(false);
    const [committeeMembersWithReviews, setCommitteeMembersWithReviews] = useState([]);
    const [requestProgressStatus, setRequestProgressStatus] = useState('');
    const [committeeReviewRequestStatus, setCommitteeReviewRequestStatus] = useState('');
    const [iframeLoading, setIframeLoading] = useState(true);
    const iframeRef = useRef(null);

    const [topBarHeight, setTopBarHeight] = useState(0);
    const headerRef = useRef(null);
    
    const sectionRefs = useRef(new Map());

    useEffect(() => {
        const getFileInfo = async () => {
            try {
                const getFileInfoLink = (role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "" : "admin";
                const route = `/api/v1/files/${getFileInfoLink}/${ectdFileId}`;
                const response = await axiosInstance.get(route);
                if (response.data && response.data.success && response?.data?.data)
                    setUploadedFile(response.data.data);
            }
            catch (error) {
                console.error('Error fetching file info:', error);
            }
        }
        if (ectdFileId) {
            getFileInfo()
        }
    }, [ectdFileId, role]);

    // fetch current user to render first section, persona customer for committee members
    useEffect(() => {
        // Fetch current user data if needed
        const fetchCurrentUser = async () => {
            try {
                const response =
                    persona === "customer"
                        ? await getCurrentUserCustomer()
                        : await getCurrentUserAdmin();

                setCurrentUser(prev => ({ ...prev, ...response }));
            } catch (error) {
                console.error("Error fetching current user:", error);
            }
        };

        if (persona)
            fetchCurrentUser();
    }, [persona]);

    const initialize = useCallback(async () => {
        try {
            const getLink = (persona === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "enduser" : "admin";
            const route = `/api/v1/committee-reviews/members/${getLink}/${requestProgressId}`;
            const response = await axiosInstance.get(route);
            if (response.data && response.status === 200) {
                setRequestProgressStatus(response.data?.request_progress_status || '');
                setCommitteeMembersWithReviews(response.data?.committeeMembersWithReviews || []);
                setCommitteeReviewRequestStatus(response.data?.committeeReviewRequestStatus || '');
                setCurrentUser(prev => ({
                    ...prev,
                    review: response.data?.committeeMembersWithReviews
                        ?.find(item => item.id === prev?.id)?.review || ''
                }));
            }
        }
        catch (error) {
            console.error('Error initializing committee reviews:', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [persona, role, requestProgressId]);

    useEffect(() => {
        if (requestProgressId) {
            initialize();
        }
    }, [requestProgressId, initialize]);

    useEffect(() => {
        if (initialUploadedFile)
            setUploadedFile(initialUploadedFile);
    }, [initialUploadedFile]);

    // Detect top bar height on mount
    useEffect(() => {
        const detectTopBarHeight = () => {
            const topBar = document.querySelector('header') || 
                           document.querySelector('[role="banner"]') ||
                           document.querySelector('.navbar') ||
                           document.querySelector('.header');
            
            if (topBar) {
                const topBarRect = topBar.getBoundingClientRect();
                setTopBarHeight(topBarRect.height);
            } else {
                setTopBarHeight(0);
            }
        };

        detectTopBarHeight();

        // Update on resize
        window.addEventListener('resize', detectTopBarHeight);
        
        return () => {
            window.removeEventListener('resize', detectTopBarHeight);
        };
    }, [committeeReviewRequestStatus]);


    useEffect(() => {
        if (activeTab === 1 && tabs.length === 3) {
            setIframeLoading(true);
        }
    }, [activeTab, tabs.length]);

    // Handle postMessage for iframe resizing
    useEffect(() => {
        const handleMessage = (event) => {
            const targetOrigin = "https://iqvia-demo.vercel.app";
            
            if (event.origin !== targetOrigin) {
                return;
            }

            if (event.data && event.data.type === "iframe-resize") {
                if (iframeRef.current && typeof event.data.iframeHeight === "number") {
                    iframeRef.current.style.height = `${event.data.iframeHeight}px`;
                }
            }
        };
        
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    const scrollToSection = useCallback((userId) => {
        const sectionElement = sectionRefs.current.get(userId);
        
        if (!sectionElement) {
            return false;
        }
        
        if (sectionElement.offsetHeight === 0 || sectionElement.children.length === 0) {
            return false;
        }

        const offsetTop = topBarHeight + 105;
        
        sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        
        setTimeout(() => {
            const elementPosition = sectionElement.getBoundingClientRect().top;
            const offsetPosition = window.pageYOffset + elementPosition - offsetTop;
            
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }, 100);
        
        return true;
    }, [topBarHeight]);

    const handleRowClick = useCallback((rowData) => {
        const userId = rowData.id;
        
        setViewStatusModalOpen(false);
        
        setTimeout(() => {
            const scrolled = scrollToSection(userId);
            
            if (!scrolled) {
                toast.warning(t('assessmentReviews.sectionNotAvailable'), {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
        }, 300);
    }, [scrollToSection, t]);

    const renderSection = (section, sectionRef = null) => {
        const sectionCategory = section?.end_users_category?.name;
        
        if (sectionCategory === CATEGORIES.CONTROL_LAB_VALIDATION) {
            if (canEditLaboratory(role) && !currentUser?.review) {
                return <div ref={sectionRef} className={styles.formSubSectionStyleContainer}>
                    <LaboratoryUpload
                        {...laboratoryUploadComponentProps}
                        user={section}
                    />
                </div>
            } else {
                return <div ref={sectionRef}>
                    <LaboratoryView
                        {...laboratoryViewComponentProps}
                        containerClassName={styles.formSubSectionStyleContainer}
                        role={role}
                        committeeReviewRequestStatus={committeeReviewRequestStatus}
                        user={section}
                    />
                </div>
            }
        }
        else if (sectionCategory === CATEGORIES.INSPECTION_DEPARTMENT) {
            if (canEditInspection(role) && !currentUser?.review) {
                return <div ref={sectionRef} className={styles.formSubSectionStyleContainer}>
                    <InspectionUpload
                        {...inspectionUploadComponentProps}
                        user={section}
                    />
                </div>
            } else {
                return <div ref={sectionRef}>
                    <InspectionView
                        {...inspectionViewComponentProps}
                        containerClassName={styles.formSubSectionStyleContainer}
                        role={role}
                        committeeReviewRequestStatus={committeeReviewRequestStatus}
                        user={section}
                    />
                </div>
            }
        } else if (CATEGORIES.ECTD_ASSESSMENT_CATEGORIES.includes(sectionCategory)) {
            return <div ref={sectionRef}>
                <CommitteeECTDSection
                    section={section}
                    ectdFileId={uploadedFile?.id}
                    role={role}
                    requestId={requestId}
                    containerClassName={styles.formSubSectionStyleContainer}
                    committeeReviewRequestStatus={committeeReviewRequestStatus}
                    currentUser={currentUser}
                    requestProgressId={requestProgressId}
                />
            </div>
        }
        return <></>;
    }

    const renderFirstSection = () => {
        if (!currentUser) return <></>;
        
        const ref = (el) => {
            if (el && currentUser.id) {
                sectionRefs.current.set(currentUser.id, el);
            }
        };
        
        return renderSection(currentUser, ref);
    }

    const renderSections = () => {
        return committeeMembersWithReviews.map((section) => {
            if (section?.id === currentUser?.id) {
                return <React.Fragment key={section.id}></React.Fragment>;
            }
            
            const ref = (el) => {
                if (el && section.id) {
                    sectionRefs.current.set(section.id, el);
                }
            };
            
            return (
                <React.Fragment key={section.id}>
                    {renderSection(section, ref)}
                </React.Fragment>
            )
        })
    }

    const getTabIcon = (tabValue) => {
        switch(tabValue) {
            case 0:
                return <NewspaperFold />;
            case 1:
                return tabs.length === 3 ? <PresentationSpeaker /> : <Planner />;
            case 2:
                return <Planner />;
            default:
                return null;
        }
    };

    const handleTabClick = (tabValue) => {
        setActiveTab(tabValue);
    };

    return (
        <div style={{ display: 'flex', gap: '20px'}}>
            <div className={tabStyles.tabsContainer}>
                {tabs.map((tab) => (
                    <div
                        key={tab.value}
                        className={`${tabStyles.tabCard} ${activeTab === tab.value ? tabStyles.active : ''}`}
                        onClick={() => handleTabClick(tab.value)}
                    >
                        <div className={tabStyles.tabIcon}>
                            {getTabIcon(tab.value)}
                        </div>
                        <div className={tabStyles.tabLabel}>
                            {tab.label}
                        </div>
                    </div>
                ))}
            </div>

                <div style={{ width: '100%' }}>

                {activeTab === 0 && uploadedFile &&
                    <div className={styles.formSubSectionStyleContainer}>
                        <EctdTree {...ectdTreeComponentProps}
                            role={role}
                            ectdFileId={uploadedFile?.id}
                            uploadedFile={uploadedFile}
                            requestId={requestId}
                        />
                    </div>
                }
                {activeTab === 0 && committeeReviewRequestStatus &&
                    <div 
                        ref={headerRef}
                        className={styles.formSubSectionStyleContainer}
                        style={{
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: '#0557D5',
                            position: 'sticky',
                            top: `${topBarHeight + 24}px`,
                            zIndex: 1000,
                            boxSizing: 'border-box',
                        }}
                    >
                        <Typography variant="bodyDefault" emphasis='high' style={{ color: '#fff' }}>
                            Assessment Reviews Status
                        </Typography>
                        <Button variant='outlined'
                            onClick={() => { setViewStatusModalOpen(true); }}
                            style={{backgroundColor: '#fff', minWidth: '175px'}}
                        >
                            View Status
                        </Button>
                    </div>
                }                
                {activeTab === 0 &&
                    committeeReviewRequestStatus &&
                    renderFirstSection()}
                {activeTab === 0 && committeeReviewRequestStatus &&

                    showCommitteeReviewButtons &&
                    requestProgressStatus === STATUSES.ROLE_STATUSES[ROLES.committee].ACTIVE &&
                    <div style={{ marginBottom: 50 }}>
                        <CommitteeReviewButtons
                            {...CommitteeReviewButtonsComponentProps}
                        />
                    </div>
                }
                {activeTab === 0 && committeeReviewRequestStatus &&
                    renderSections()}
                
                {activeTab === 1 && tabs.length === 3 && (
                   <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                       <Paper elevation={1} sx={{ padding: 4, margin: 0, width: '100%' }}>
                        {iframeLoading && (
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'flex-start', 
                                minHeight: '500px',
                                width: '100%',
                                zIndex: 1,
                            }}>
                                <CircularProgress variant="indeterminate" statusText="Loading..." />
                            </Box>
                        )}

                        <iframe
                            ref={iframeRef}
                            src="https://iqvia-demo.vercel.app"
                            width="100%"
                            scrolling="no"
                            style={{ 
                                border: 'none',
                                overflow: 'hidden',
                                display: 'block',
                                minHeight: '500px',
                                height: '500px'
                            }}
                            title="AI Review Agent"
                            onLoad={() => setIframeLoading(false)}
                            />
                            </Paper>
                    </Box>
                )}
                
                {((activeTab === 1 && tabs.length === 2) || (activeTab === 2 && tabs.length === 3)) && (
                    <Paper elevation={1} sx={{ padding: 4, margin: 0, width: '100%' }}>
                        <EctdVersionsHistory
                            requestId={requestId}
                            persona={role === 'customer' || CATEGORIES.COMMITTEE_CATEGORIES.includes(role) ? 'customer' : 'admin'}
                            role={role}
                            />
                    </Paper>
                )}
                </div>
            <Modal
                title=""
                open={viewStatusModalOpen}
                onClose={() => setViewStatusModalOpen(false)}
                hideButtons
                showCloseIcon={false}
            >
                <MembersStatuses
                    {...MembersStatusesComponentProps}
                    data={committeeMembersWithReviews}
                    onRowClick={handleRowClick}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 30 }}>
                    <Button variant="primary"
                        onClick={() => { setViewStatusModalOpen(false); }}
                    >
                        Close
                    </Button>
                </div>
            </Modal>
        </div>);
};

export default Ectd;